import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { canAccessPath } from './utils/permissions';
import Layout from './components/layout/Layout';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import Tasks from './pages/Tasks';
import Board from './pages/Board';
import Availability from './pages/Availability';
import Sprints from './pages/Sprints';
import Clients from './pages/Clients';
import Leads from './pages/Leads';
import Deals from './pages/Deals';
import TimeLogs from './pages/TimeLogs';
import Activities from './pages/Activities';
import Users from './pages/Users';
import UserDetails from './pages/UserDetails';
import TeamDetails from './pages/TeamDetails';
import Notifications from './pages/Notifications';
import Chat from './pages/Chat';
import { ChatProvider } from './context/ChatContext';
import Feedback from './pages/Feedback';
import ProjectActivityLog from './pages/ProjectActivityLog';
import Reports from './pages/Reports';
import AIDashboard from './pages/AIDashboard';
import ProjectDashboard from './pages/ProjectDashboard';
import Webhooks from './pages/Webhooks';

import ResetPassword from './pages/ResetPassword';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh' }}>
      <div style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-display)' }}>Loading...</div>
    </div>
  );
  // Unauthenticated → back to landing page
  return user ? children : <Navigate to="/" />;
};

const RoleRoute = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (!canAccessPath(user?.role, location.pathname)) {
    return <Navigate to="/app" replace />;
  }
  return children;
};

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/"        element={<LandingPage />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/login"   element={<Login />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          {/* Protected app routes — all live under /app */}
          <Route path="/app" element={<ProtectedRoute><ChatProvider><Layout /></ChatProvider></ProtectedRoute>}>
            <Route index                element={<Dashboard />} />
            <Route path="ai-dashboard"   element={<AIDashboard />} />
            <Route path="projects"      element={<RoleRoute><Projects /></RoleRoute>} />
            <Route path="projects/:id/dashboard" element={<RoleRoute><ProjectDashboard /></RoleRoute>} />
            <Route path="tasks"         element={<RoleRoute><Tasks /></RoleRoute>} />
            <Route path="board"         element={<RoleRoute><Board /></RoleRoute>} />
            <Route path="availability"  element={<RoleRoute><Availability /></RoleRoute>} />
            <Route path="sprints"       element={<RoleRoute><Sprints /></RoleRoute>} />
            <Route path="clients"       element={<RoleRoute><Clients /></RoleRoute>} />
            <Route path="clients/:id"   element={<RoleRoute><UserDetails /></RoleRoute>} />
            <Route path="leads"         element={<RoleRoute><Leads /></RoleRoute>} />
            <Route path="deals"         element={<RoleRoute><Deals /></RoleRoute>} />
            <Route path="timelogs"      element={<RoleRoute><TimeLogs /></RoleRoute>} />
            <Route path="activities"    element={<RoleRoute><Activities /></RoleRoute>} />
            <Route path="users"         element={<RoleRoute><Users /></RoleRoute>} />
            <Route path="users/:id"     element={<RoleRoute><UserDetails /></RoleRoute>} />
            <Route path="teams/:id"     element={<RoleRoute><TeamDetails /></RoleRoute>} />
            <Route path="chat"          element={<RoleRoute><Chat /></RoleRoute>} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="feedback" element={<RoleRoute><Feedback /></RoleRoute>} />
            <Route path="project-activity-log" element={<RoleRoute><ProjectActivityLog /></RoleRoute>} />
            <Route path="reports" element={<RoleRoute><Reports /></RoleRoute>} />
            <Route path="webhooks" element={<RoleRoute><Webhooks /></RoleRoute>} />
          </Route>

          {/* Catch-all → landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}